/**
 * PATH: apps/quote/src/components/builder/scope-sheet-grid/SectionHeaderRow.tsx
 * PURPOSE: Collapsible section header row inside the scope sheet grid.
 */

import { useState, useRef } from 'react'
import { ChevronRight, ChevronDown, GripVertical, Edit3, Trash2, Focus, X } from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import type { QuoteSection } from '@/lib/quoteApi'

interface SectionHeaderRowProps {
  section: QuoteSection
  isCollapsed: boolean
  onToggleCollapse: () => void
  onRename: (newTitle: string) => void
  onDelete: () => void
  onFocus: () => void
  isFocused: boolean
  isEditable: boolean
}

export default function SectionHeaderRow({
  section,
  isCollapsed,
  onToggleCollapse,
  onRename,
  onDelete,
  onFocus,
  isFocused,
  isEditable,
}: SectionHeaderRowProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(section.title)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleStartEdit = () => {
    if (!isEditable) return
    setIsEditing(true)
    setEditValue(section.title)
    setTimeout(() => {
      inputRef.current?.focus()
      inputRef.current?.select()
    }, 0)
  }

  const handleSave = () => {
    const trimmed = editValue.trim()
    if (trimmed && trimmed !== section.title) {
      onRename(trimmed)
    }
    setIsEditing(false)
  }

  const itemCount = section.items?.length ?? 0
  const subtotal = parseFloat(String(section.section_subtotal)) || 0

  return (
    <div className="flex items-center h-full bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 group">
      <button
        onClick={onToggleCollapse}
        className="w-10 h-full flex items-center justify-center hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
      >
        {isCollapsed ? <ChevronRight className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
      </button>

      <div className="w-6 flex items-center justify-center cursor-grab opacity-0 group-hover:opacity-100 transition-opacity">
        <GripVertical className="w-3 h-3 text-slate-400" />
      </div>

      <div className="flex-1 flex items-center gap-3 min-w-0 px-2">
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave()
              if (e.key === 'Escape') setIsEditing(false)
            }}
            className="flex-1 px-2 py-1 text-sm font-semibold bg-white dark:bg-slate-900 border border-blue-400 rounded outline-none"
          />
        ) : (
          <>
            <span
              className={cn('font-semibold text-slate-900 dark:text-white truncate', isEditable && 'cursor-pointer hover:text-blue-600')}
              onClick={handleStartEdit}
            >
              {section.title}
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {itemCount} item{itemCount !== 1 ? 's' : ''}
            </span>
          </>
        )}
      </div>

      <div className="w-32 text-right pr-4">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{formatCurrency(subtotal)}</span>
      </div>

      {!isEditing && (
        <div className="flex items-center gap-1 pr-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={onFocus}
            className={cn(
              'p-1.5 rounded',
              isFocused ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600' : 'hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500'
            )}
            title={isFocused ? 'Exit focus' : 'Focus on this section'}
          >
            {isFocused ? <X className="w-3.5 h-3.5" /> : <Focus className="w-3.5 h-3.5" />}
          </button>
          {isEditable && (
            <>
              <button onClick={handleStartEdit} className="p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700" title="Rename section">
                <Edit3 className="w-3.5 h-3.5 text-slate-500" />
              </button>
              <button onClick={onDelete} className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/50" title="Delete section">
                <Trash2 className="w-3.5 h-3.5 text-red-500" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
